
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Shield, Users, Calendar, Settings } from 'lucide-react';
import ChangePasswordForm from '@/components/auth/ChangePasswordForm';
import UpdatePinForm from '@/components/auth/UpdatePinForm';
import InternalUserProfile from './InternalUserProfile';

const ProfileForm: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [activeForm, setActiveForm] = useState<'password' | 'pin' | null>(null);

  // Determine which type of user we're dealing with
  const isInternalUser = !!internalUser && !supabaseUser;
  const user = supabaseUser || internalUser;

  if (!user) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <p className="text-gray-400 text-center">
          No user data available. Please log in.
        </p>
      </div>
    );
  }

  // If it's an internal user, use the InternalUserProfile component
  if (isInternalUser) {
    return <InternalUserProfile />;
  }

  // Handle form displays for Supabase users
  if (activeForm === 'password') {
    return (
      <ChangePasswordForm 
        onBack={() => setActiveForm(null)} 
        isInternal={false}
      />
    );
  }

  if (activeForm === 'pin') {
    return (
      <UpdatePinForm onBack={() => setActiveForm(null)} />
    );
  }

  const getRoleDisplayName = (role: string) => {
    const roleMap = {
      'player': 'Player',
      'coach': 'Coach',
      'team-owner': 'Team Owner',
      'venue': 'Venue Manager',
      'creative': 'Creative',
      'support': 'Support/Score Keeper',
      'fan': 'Fan',
      'parent': 'Parent'
    };
    return roleMap[role] || role;
  };

  const getAgeGroupDisplayName = (ageGroup: string) => {
    const ageGroupMap = {
      '1': 'Age 1', '2': 'Age 2', '3': 'Age 3', '4': 'Age 4', '5': 'Age 5',
      '6': 'Age 6', '7': 'Age 7', '8': 'Age 8', '9': 'Age 9', '10': 'Age 10',
      '11': 'Age 11', '12': 'Age 12', '13': 'Age 13',
      '13-and-under': '13 and Under',
      '14-17': '14-17 Years',
      '18-plus': '18+ Years'
    };
    return ageGroupMap[ageGroup] || ageGroup;
  };

  // Supabase user profile display - we know user is a Supabase User here
  const supabaseUserData = user as any; // Safe to cast since we know it's a Supabase user

  return (
    <div className="space-y-6">
      {/* Account Overview */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <User className="h-6 w-6" />
            Account Information
          </CardTitle>
          <p className="text-gray-400">
            Your Old School account details and settings
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Email</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">{supabaseUserData.email}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Username</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">{profile?.username || 'Not set'}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">First Name</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">{profile?.first_name || 'Not set'}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Last Name</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">{profile?.last_name || 'Not set'}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Account Role</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-sports-green" />
                  <span className="text-white">{getRoleDisplayName(profile?.role || 'player')}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Age Group</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Users className="h-4 w-4 text-sports-green" />
                  <span className="text-white">{getAgeGroupDisplayName(profile?.age_group || '18-plus')}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Account Created</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-sports-green" />
                  <span className="text-white">
                    {new Date(supabaseUserData.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Email Verified</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    supabaseUserData.email_confirmed_at 
                      ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                      : 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/30'
                  }`}>
                    {supabaseUserData.email_confirmed_at ? 'Verified' : 'Pending Verification'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Information */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Security Settings
          </CardTitle>
          <p className="text-gray-400">
            Your account security status and settings
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2">🔐 Security Features</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Email & Password Authentication</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Email Verification</span>
                <span className={supabaseUserData.email_confirmed_at ? 'text-green-400' : 'text-yellow-400'}>
                  {supabaseUserData.email_confirmed_at ? '✓ Verified' : '⚠ Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Password Reset via Email</span>
                <span className="text-green-400">✓ Available</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">Account Actions</CardTitle>
          <p className="text-gray-400">
            Manage your account settings
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => setActiveForm('password')}
              variant="outline" 
              className="border-sports-green text-sports-green hover:bg-sports-green/10"
            >
              Change Password
            </Button>
            <Button variant="outline" className="border-gray-600 text-gray-400 hover:bg-gray-700">
              Update Profile
            </Button>
            <Button variant="outline" className="border-gray-600 text-gray-400 hover:bg-gray-700">
              Export Account Data
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Note: PIN functionality is only available for New School accounts.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileForm;
